import axios from "../plugins/axios";
import type { AxiosRequestConfig } from "axios";

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

export interface RiskAssessmentTurn {
    // null/empty on the first message — the backend creates a new OpenAI
    // conversation and returns its id for subsequent turns.
    conversationId: string | null;
    // the model's JSON string: { mode, message, questions, assessment }
    content: string | null;
}

/**
 * Sends the user's latest message to the conversational RA generator endpoint.
 * First call: pass conversationId = null.
 */
const sendMessage = async (
    conversationId: string | null,
    message: string,
): Promise<RiskAssessmentTurn> => {
    try {
        const { data } = await axios.post<RiskAssessmentTurn>(
            `${apiUrl}/AI/GenerateRiskAssessment`,
            { conversationId, message },
            // handle errors inline in the chat rather than via the global snack toast
            { errorHandle: false } as AxiosRequestConfig & { errorHandle?: boolean },
        );
        return data;
    } catch (e: any) {
        return Promise.reject(
            e?.response?.data?.error || e?.message || "Could not reach the risk assessment assistant.",
        );
    }
};

export interface SavedRiskAssessmentSummary {
    id: number;
    title: string | null;
    description: string | null;
    ownerEmail: string | null;
    createdOn: string;
    lastModifiedOn: string | null;
}

export interface SavedRiskAssessmentDetail extends SavedRiskAssessmentSummary {
    assessmentJson: string | null;
}

// Handle errors inline in the components rather than via the global snack toast.
const noSnack = { errorHandle: false } as AxiosRequestConfig & { errorHandle?: boolean };

/** Create (id = null) or update-in-place (id set) a saved assessment. */
const saveToLibrary = async (payload: {
    id: number | null;
    title: string;
    description: string;
    assessmentJson: string;
}): Promise<{ id: number }> => {
    try {
        const { data } = await axios.post<{ id: number }>(
            `${apiUrl}/AI/SaveRiskAssessment`,
            payload,
            noSnack,
        );
        return data;
    } catch (e: any) {
        return Promise.reject(e?.response?.data?.error || e?.message || "Could not save to the library.");
    }
};

/** The shared library — everyone with access sees all saved assessments. */
const listLibrary = async (): Promise<SavedRiskAssessmentSummary[]> => {
    try {
        const { data } = await axios.get<SavedRiskAssessmentSummary[]>(
            `${apiUrl}/AI/RiskAssessments`,
            noSnack,
        );
        return data;
    } catch (e: any) {
        return Promise.reject(e?.response?.data?.error || e?.message || "Could not load the library.");
    }
};

/** One saved assessment incl. its JSON, for preview / download. */
const getLibraryItem = async (id: number): Promise<SavedRiskAssessmentDetail> => {
    try {
        const { data } = await axios.get<SavedRiskAssessmentDetail>(
            `${apiUrl}/AI/RiskAssessment/${id}`,
            noSnack,
        );
        return data;
    } catch (e: any) {
        return Promise.reject(e?.response?.data?.error || e?.message || "Could not load that assessment.");
    }
};

const riskAssessmentToolService = {
    sendMessage,
    saveToLibrary,
    listLibrary,
    getLibraryItem,
};

export default riskAssessmentToolService;
